import type { RegimeDependenceProfileReport } from '@/types/appRouter';
import { Card, Tooltip } from '@/components/UI';
import { buildInsufficientEvidenceChecklist, type EvidenceChecklistRow } from './regimeProfilePresentation';

function ChecklistRow({ row }: { row: EvidenceChecklistRow }) {
    const line = (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
            <span aria-hidden className={row.passed ? 'w-4 text-green-400' : 'w-4 text-red-400'}>
                {row.passed ? '✓' : '✗'}
            </span>
            <span className="sr-only">{row.passed ? 'Passed:' : 'Failed:'}</span>
            <span className="min-w-56 text-zinc-300">{row.label}</span>
            <span className={row.passed ? 'text-zinc-400' : 'font-medium text-red-300'}>{row.actualDisplay}</span>
            <span className="text-zinc-500">({row.requiredDisplay})</span>
        </div>
    );

    return row.detail !== undefined ? <Tooltip content={row.detail}>{line}</Tooltip> : line;
}

interface RegimeInsufficientEvidenceChecklistProps {
    report: Pick<RegimeDependenceProfileReport, 'dimensions' | 'thresholds' | 'verdict'>;
}

/**
 * Layer 1b — `insufficient_evidence` as a first-class checklist, not an error. Each gate shows its
 * actual value against the report's own echoed threshold so the user knows which lever to pull.
 */
export function RegimeInsufficientEvidenceChecklist({ report }: RegimeInsufficientEvidenceChecklistProps) {
    const rows = buildInsufficientEvidenceChecklist(report);

    return (
        <Card className="bg-zinc-900 border border-zinc-800">
            <h3 className="text-sm font-medium text-zinc-100">Why the evidence is insufficient</h3>
            <div className="mt-3 space-y-1.5">
                {rows.map((row) => (
                    <ChecklistRow key={row.code} row={row} />
                ))}
            </div>
            <p className="mt-3 text-sm text-zinc-400">
                This regime check is inconclusive. Continue other promising treatments; expand the data only if another regime check would
                help your decision.
            </p>
        </Card>
    );
}
